import React from 'react'
import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { BusAttendeeBase } from './type' // adjust path if needed
import toast from 'react-hot-toast'
import { createConductor, updateConductor } from './api'
import { handleApiError } from '../../../helpers/errorHandler'
import FormikControl from '../../../components/formikControl/FormikControl'
import { getCountryCallingCode, isPossiblePhoneNumber } from 'react-phone-number-input'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'
import { startOfToday } from 'date-fns'
import { Col, Row } from 'react-bootstrap'
import CountrySelect from '../../../components/CountryDropdown'

interface BusAttendeeFormProps {
  initialValues?: BusAttendeeBase
  onAfterSubmit: () => void
}

type FormValues = Omit<BusAttendeeBase, 'conductor_id' | 'nfc_code'> & {
  countryCode?: string
  conductor_mobile?: string // allow flexible mobile field name
}

const formInitialValues: FormValues = {
  conductor_name: '',
  conductor_mobile: '',
  conductor_status: '1',
  conductor_reg_num: '',
  blood_group: '',
  license_expiry_date: null,
  passport: '',
  passport_expiry: '',
  passport_country: '',
  qatar_id: '',
  qatar_id_expiry: '',
  countryCode: '',
}

const validationSchema: Yup.Schema<FormValues> = Yup.object({
  conductor_name: Yup.string()
    .max(100, 'The Bus Attendee name cannot exceed 100 characters')
    .required('Bus Attendee Name is required'),
  conductor_status: Yup.string()
    .oneOf(['0', '1'], 'Status must be either "0" or "1"')
    .required('Status is required'),
  conductor_mobile: Yup.string()
    .test('conductor_mobile', 'Enter a valid phone number', (value) => {
      if (value) return isPossiblePhoneNumber(value)
      return false
    })
    .required('Mobile Number is required'),
  countryCode: Yup.string().trim().required('Country Code is required'),
  conductor_reg_num: Yup.string().nullable(),
  blood_group: Yup.string().nullable(),
  passport: Yup.string().nullable(),
  passport_expiry: Yup.date()
    .min(startOfToday(), 'Passport Expiry cannot be in the past')
    .nullable(),
  passport_country: Yup.string().nullable(),
  qatar_id: Yup.string().nullable(),
  qatar_id_expiry: Yup.date()
    .min(startOfToday(), 'Qatar Expiry cannot be in the past')
    .nullable(),
})

function BusAttendeeForm({ initialValues, onAfterSubmit }: BusAttendeeFormProps) {
  const formType = initialValues ? 'update' : 'add'

  // map API -> form values and normalize mobile with +<countryCode> if API provided a country_code
  const formValues: FormValues = initialValues
    ? ((): FormValues => {
        const api: any = initialValues
        const apiCountryCode = api.country_code ?? api.conductor_country_code ?? ''
        let apiMobile = api.conductor_mobile ?? api.mobile ?? ''

        if (apiCountryCode && apiMobile) {
          const plusCode = `+${String(apiCountryCode)}`
          if (!apiMobile.startsWith('+') && !apiMobile.startsWith(plusCode)) {
            apiMobile = plusCode + apiMobile.replace(/^0+/, '')
          }
        }

        return {
          ...(api as any),
          countryCode: String(apiCountryCode || ''),
          conductor_mobile: apiMobile || '',
        }
      })()
    : formInitialValues

  const submitHandler = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    const toastId = toast.loading('Submitting')
    const action = formType === 'add' ? createConductor : updateConductor

    // Normalize date fields to null if empty string
    const dateFields = ['passport_expiry', 'qatar_id_expiry', 'license_expiry_date']
    dateFields.forEach((field) => {
      if ((values as any)[field] === '') (values as any)[field] = null
    })

    // Ensure mobile is E.164-like: starts with '+', if countryCode exists and mobile doesn't start with '+', prefix it
   const payload: any = {
    ...values,
     country_code: values.countryCode ?? null,
    license_expiry_date:null
  }
  //delete (payload as any).countryCode

  if (payload.conductor_mobile && payload.countryCode) {
  const regex = new RegExp(`^\\+?${payload.countryCode}`)
  payload.conductor_mobile = payload.conductor_mobile.replace(regex, '')
}

delete payload.countryCode

    try {
      await action(payload)
      toast.success('Success', { id: toastId })
      onAfterSubmit()
    } catch (err: any) {
      handleApiError(err, toastId)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="px-5 py-3">
      <Formik<FormValues>
        initialValues={formValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={submitHandler}
      >
        {({ isSubmitting, resetForm, dirty, isValid, values, setFieldValue }) => (
          <Form>
            <Row>
              <Col md="4">
                <FormikControl
                  control="input"
                  required
                  placeholder="Bus Attendee Name"
                  label="Bus Attendee Name"
                  name="conductor_name"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="phone-input"
                  required
                  placeholder="Mobile Number"
                  label="Mobile Number"
                  name="conductor_mobile"
                  value={values.conductor_mobile} // full value, e.g. +97455229900
                  onChange={(value: string | undefined) => {
                    setFieldValue('conductor_mobile', value ?? '')
                  }}
                  onCountryChange={(isoCountry: any) => {
                    if (isoCountry) {
                      const code = getCountryCallingCode(isoCountry)
                      setFieldValue('countryCode', code) // store country code separately
                    } else {
                      setFieldValue('countryCode', '')
                    }
                  }}
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="check"
                  type="radio"
                  options={[
                    { key: 'Active', value: '1' },
                    { key: 'Inactive', value: '0' },
                  ]}
                  required
                  placeholder="Active Status"
                  label="Active Status"
                  name="conductor_status"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  placeholder="Employee ID"
                  label="Employee ID"
                  name="conductor_reg_num"
                />
              </Col>

              <Col md="4">
                <FormikControl control="input" placeholder="Q ID" label="Q ID" name="qatar_id" />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="Q ID Expiry Date"
                  label="Q ID Expiry Date"
                  name="qatar_id_expiry"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="select"
                  placeholder="Blood Group"
                  label="Blood Group"
                  name="blood_group"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </FormikControl>
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  placeholder="Passport Number"
                  label="Passport Number"
                  name="passport"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="Passport Expiry Date"
                  label="Passport Expiry Date"
                  name="passport_expiry"
                />
              </Col>

              <Col md="4">
                <CountrySelect
                  placeholder="Country"
                  label="Passport Issued Country"
                  name="passport_country"
                  value={{ label: values.passport_country, value: values.passport_country }}
                  onChange={(selected: any) => {
                    setFieldValue('passport_country', selected?.value ?? '')
                    // optionally keep phone countryCode in sync with passport country selection
                    if (selected?.value) {
                      try {
                        const cc = getCountryCallingCode(selected.value)
                        setFieldValue('countryCode', cc)
                      } catch (e) {
                        // ignore
                      }
                    }
                  }}
                />
              </Col>
            </Row>

            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm()} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default BusAttendeeForm
